import ListSubheader from '@mui/material/ListSubheader'
import Popper from '@mui/material/Popper'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import Autocomplete from '@mui/material/Autocomplete'
import autocompleteClasses from '@mui/material/Autocomplete/autocompleteClasses'
/* eslint-disable @typescript-eslint/no-explicit-any */
import { skillAPI } from 'api/SkillAPI'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import CaleoInputLabel from 'Components/reusable/CaleoCustomComponents/CaleoInputLabel'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ISkillOrIndustryOrRole } from 'types/cvsInterfaces'
import { IError } from 'types/error'
import { chooseDBTranslation } from 'utils/translations'
import { collapseDuplicateOptions, fuseFiltering, normalizeOptionName } from 'utils/utils'
import { InputField } from '../InputField'
import { orderBy } from 'lodash'
import { List, RowComponentProps, useListRef, ListImperativeAPI } from 'react-window'
import i18n from 'i18n'
import { useDidYouMean } from '../DidYouMeanDialog/useDidYouMean'

const LISTBOX_PADDING = 8

type ItemData = Array<
  | {
      key: number
      group: string
      children: React.ReactNode
    }
  | [React.ReactElement, string, number]
>

function RowComponent({ index, itemData, style }: RowComponentProps & { itemData: ItemData }) {
  const dataSet = itemData[index]
  const inlineStyle = {
    ...style,
    top: ((style.top as number) ?? 0) + LISTBOX_PADDING,
  }

  if ('group' in dataSet) {
    return (
      <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
        {dataSet.group}
      </ListSubheader>
    )
  }

  const { key, ...optionProps } = dataSet[0]

  return (
    <Typography key={key} component="li" {...optionProps} noWrap style={inlineStyle}>
      {dataSet[0].key}
    </Typography>
  )
}

// Adapter for react-window v2
const ListboxComponent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement> & {
    internalListRef: React.Ref<ListImperativeAPI>
    onItemsBuilt: (optionIndexMap: Map<string, number>) => void
  }
>(function ListboxComponent(props, ref) {
  const { children, internalListRef, onItemsBuilt, ...other } = props
  const itemData: ItemData = []
  const optionIndexMap = React.useMemo(() => new Map<string, number>(), [])

  ;(children as ItemData).forEach(item => {
    itemData.push(item)
    if ('children' in item && Array.isArray(item.children)) {
      itemData.push(...item.children)
    }
  })

  // Map option values to their indices in the flattened array
  itemData.forEach((item, index) => {
    if (Array.isArray(item) && item[1]) {
      optionIndexMap.set(item[1], index)
    }
  })

  React.useEffect(() => {
    if (onItemsBuilt) {
      onItemsBuilt(optionIndexMap)
    }
  }, [onItemsBuilt, optionIndexMap])

  const theme = useTheme()
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
    noSsr: true,
  })
  const itemCount = itemData.length
  const itemSize = smUp ? 36 : 48

  const getChildSize = (child: ItemData[number]) => {
    if (Object.hasOwn(child, 'group')) {
      return 48
    }
    return itemSize
  }

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0)
  }

  // Separate className for List, other props for wrapper div (ARIA, handlers)
  const { className, ...otherProps } = other

  return (
    <div ref={ref} {...otherProps}>
      <List
        className={className}
        listRef={internalListRef}
        key={itemCount}
        rowCount={itemCount}
        rowHeight={index => getChildSize(itemData[index])}
        rowComponent={RowComponent}
        rowProps={{ itemData }}
        style={{
          height: getHeight() + 2 * LISTBOX_PADDING,
          width: '100%',
        }}
        overscanCount={5}
        tagName="ul"
      />
    </div>
  )
})

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
})

type SingleValue = {
  multiple?: false
  value: ISkillOrIndustryOrRole | null
  onChange: (newValue: ISkillOrIndustryOrRole | null) => void
}

type MultiValue = {
  multiple: true
  value: ISkillOrIndustryOrRole[]
  onChange: (newValue: ISkillOrIndustryOrRole[]) => void
}

type Value = SingleValue | MultiValue

const getLabel = (item: ISkillOrIndustryOrRole) => {
  const name = chooseDBTranslation(i18n, item).name
  const languages: string[] = []
  for (const currentItem of item.translations) {
    languages.push(currentItem.Language.name.toUpperCase())
  }
  return `${name} (${languages}) `
}

/**
 * Skill picker component.
 *
 * @param label - The label text.
 * @param size - The size of the component.
 * @param required - Whether the field is required.
 * @param disabled - Whether the field is disabled.
 * @param name - The name of the field.
 * @param usedSkills - The used skills.
 * @param noPlaceholders - Whether to show placeholders.
 * @param placeholderLabel - The placeholder label text.
 * @param placeholderText - The placeholder text.
 * @param onKeyDown - The onKeyDown event handler.
 * @param type - The type of the skill.
 * @param adminView - Whether the field is for admin view.
 * @param icon - The icon of the field.
 * @param disableClearable - Whether the field is disableClearable.
 * @param search - Whether the field is searchable.
 * @param showUnpromoted - Whether to show unpromoted skills.
 * @param allowCreate - Whether a typed name that matches no option can be created; false only picks existing ones.
 * @returns The skill picker component.
 * @notExported
 */
const SkillPicker: React.FC<
  Value & {
    label?: string
    size?: 'small' | 'medium'
    required?: boolean
    disabled?: boolean
    name?: string
    usedSkills?: ISkillOrIndustryOrRole[]
    usedSkillIds?: number[]
    noPlaceholders?: boolean
    placeholderLabel?: string
    placeholderText?: string
    onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void
    type: string
    adminView?: boolean
    icon?: React.ReactElement
    disableClearable?: boolean
    search?: boolean
    showUnpromoted?: boolean
    allowCreate?: boolean
    options?: ISkillOrIndustryOrRole[]
    insetLabel?: boolean
    resetOnSelect?: boolean
    sx?: object
  }
> = ({
  multiple,
  size = 'small',
  value,
  onChange,
  label = 'Skill',
  required = false,
  disabled = false,
  name,
  usedSkills = [],
  usedSkillIds = [],
  noPlaceholders = false,
  placeholderLabel,
  placeholderText,
  onKeyDown,
  type,
  adminView = false,
  icon,
  disableClearable = false,
  search = false,
  showUnpromoted = false,
  allowCreate = true,
  options,
  insetLabel,
  resetOnSelect = false,
  sx,
}) => {
  const { t, i18n } = useTranslation()
  const [backendError, setBackendError] = useState<IError>()
  const [skills, setSkills] = useState<ISkillOrIndustryOrRole[]>([])
  const [unpromotedSkills, setUnpromotedSkills] = useState<ISkillOrIndustryOrRole[]>([])
  const [nameValue, setNameValue] = useState<string | undefined>(name ?? '')
  const [helperText, setHelperText] = useState<string>()
  const [componentValue, setComponentValue] = useState<Value['value']>(value ?? null)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    const controller = new AbortController()

    if (options) {
      setUnpromotedSkills(options)
      setSkills(options)
      return
    }

    if (showUnpromoted && unpromotedSkills.length > 0) {
      return
    }

    if (!showUnpromoted && skills.length > 0) {
      return
    }

    if (!options) {
      ;(async () => {
        try {
          let newSkills: ISkillOrIndustryOrRole[] = []

          if (type === 'skill') {
            if (showUnpromoted) {
              newSkills = await skillAPI.getAllUnpromotedSkills(controller)
            } else {
              newSkills = await skillAPI.getAllSkills(controller)
            }
          }

          if (type === 'industry') {
            if (showUnpromoted) {
              newSkills = await skillAPI.getAllUnpromotedIndustries(controller)
            } else {
              newSkills = await skillAPI.getAllIndustries(controller)
            }
          }

          if (type === 'role') {
            if (showUnpromoted) {
              newSkills = await skillAPI.getAllUnpromotedRoles(controller)
            } else {
              newSkills = await skillAPI.getAllRoles(controller)
            }
          }

          if (type === 'all') {
            if (showUnpromoted) {
              newSkills = await skillAPI.getAllUnpromoted(controller)
            } else {
              newSkills = await skillAPI.getAll(controller)
            }
          }
          if (noPlaceholders) newSkills = newSkills.filter(skill => skill.isPromoted === true)

          if (showUnpromoted) {
            const orderedSkills = orderBy(newSkills, [skill => chooseDBTranslation(i18n, skill).name], ['asc'])
            setUnpromotedSkills(orderedSkills)
          } else {
            const orderedSkills = orderBy(newSkills, [skill => chooseDBTranslation(i18n, skill).name], ['asc'])
            setSkills(orderedSkills)
          }
        } catch (error) {
          setBackendError(error as IError)
        }
      })()
    }

    return () => {
      controller.abort()
    }
  }, [i18n, noPlaceholders, options, showUnpromoted, skills.length, type, unpromotedSkills.length])

  useEffect(() => {
    if (name) {
      setNameValue(name)
    }
  }, [name])

  useEffect(() => {
    if (!resetOnSelect) {
      if (multiple) {
        setNameValue('')
      }
      setComponentValue(value)
      if (value && !multiple) {
        setNameValue(chooseDBTranslation(i18n, value).name)
      }
    }
  }, [i18n, multiple, resetOnSelect, value])

  // Use react-window v2's useListRef hook for imperative API access
  const internalListRef = useListRef(null)
  const optionIndexMapRef = React.useRef<Map<string, number>>(new Map())

  const handleItemsBuilt = React.useCallback((optionIndexMap: Map<string, number>) => {
    optionIndexMapRef.current = optionIndexMap
  }, [])

  // Ids to prefer when they have duplicates, as a stable key so the options array (and the
  // fuseFiltering cache keyed by it) only changes when they do
  const keepIdsKey = [
    ...usedSkills.map(item => item.id),
    ...usedSkillIds,
    ...(Array.isArray(value) ? value : value ? [value] : []).map(item => item.id),
  ]
    .sort((a, b) => a - b)
    .join(',')

  // One option per name, preferring a promoted one; admins see every duplicate (e.g. when merging)
  const shownSkills = useMemo(() => {
    const list = showUnpromoted ? unpromotedSkills : skills
    return adminView
      ? list
      : collapseDuplicateOptions(
          list,
          skill => chooseDBTranslation(i18n, skill)?.name,
          keepIdsKey ? keepIdsKey.split(',').map(Number) : []
        )
  }, [adminView, i18n, keepIdsKey, showUnpromoted, skills, unpromotedSkills])

  const createSkill = async (name: string) => {
    let newItem: ISkillOrIndustryOrRole
    if (type === 'skill') {
      newItem = await skillAPI.createSkill({ name, language: i18n.language, kind: 'skill' })
    } else if (type === 'industry') {
      newItem = await skillAPI.createSkill({ name, language: i18n.language, kind: 'industry' })
    } else {
      newItem = await skillAPI.createSkill({ name, language: i18n.language, kind: 'role' })
    }

    // The backend returns the existing skill when one already has the same name apart from case or
    // punctuation, so it may already be in the list.
    if (!skills.some(skill => skill.id === newItem.id)) {
      setSkills([...skills, newItem])
    }
    return newItem
  }

  // Used or already selected skills can't be picked again, also from the "Did you mean" dialog
  const isSkillDisabled = (skill: ISkillOrIndustryOrRole) =>
    usedSkills.some(item => item.id === skill.id) ||
    usedSkillIds.includes(skill.id) ||
    (Array.isArray(value) && value.some(item => item.id === skill.id))

  const didYouMean = useDidYouMean({
    enabled: !adminView,
    options: shownSkills,
    getNames: skill => skill.translations.map(translation => translation.name),
    getLabel: skill => chooseDBTranslation(i18n, skill).name,
    isDisabled: isSkillDisabled,
  })

  // A name typed in the picker: an existing skill picked from the "Did you mean" dialog, a new skill,
  // or null when the user cancels or creating fails
  const resolveNewSkill = async (name: string): Promise<ISkillOrIndustryOrRole | null> => {
    const choice = await didYouMean.ask(name)
    if (!choice) return null
    if ('option' in choice) return choice.option

    setCreating(true)
    try {
      return await createSkill(name)
    } catch (error) {
      setBackendError(error as IError)
      return null
    } finally {
      setCreating(false)
    }
  }
  const labelId = React.useId()

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  return (
    <>
      {!placeholderLabel && !insetLabel && (
        <CaleoInputLabel id={labelId} icon={icon} label={label} required={required} />
      )}
      <Autocomplete
        data-testid={`autocomplete-${type}-picker`}
        selectOnFocus
        clearOnBlur
        disableClearable={disableClearable}
        blurOnSelect
        handleHomeEndKeys
        multiple={multiple}
        disabled={disabled || creating}
        inputValue={nameValue}
        value={(componentValue as typeof componentValue & string) ?? ''}
        onKeyDown={e => onKeyDown && onKeyDown(e)}
        disableListWrap
        options={shownSkills as ((typeof skills)[0] | string)[]}
        getOptionDisabled={option =>
          usedSkills.some(skill => skill.id === (option as unknown as ISkillOrIndustryOrRole)?.id) ||
          (typeof option !== 'string' && usedSkillIds.includes(option.id))
        }
        onChange={async (_event, newValue) => {
          if (typeof newValue === 'string' || Array.isArray(newValue)) {
            let newSkill: ISkillOrIndustryOrRole[] | ISkillOrIndustryOrRole
            if (Array.isArray(newValue)) {
              const items: ISkillOrIndustryOrRole[] = []
              for (const skill of newValue) {
                const item = typeof skill === 'string' ? await resolveNewSkill(skill) : skill
                if (item && !items.some(added => added.id === item.id)) {
                  items.push(item)
                }
              }
              newSkill = items
            } else {
              const item = allowCreate ? await resolveNewSkill(newValue) : null
              if (!item) return
              newSkill = item
            }
            onChange(
              (multiple
                ? [...(newSkill as ISkillOrIndustryOrRole[])]
                : (newSkill as ISkillOrIndustryOrRole)) as unknown as ISkillOrIndustryOrRole & ISkillOrIndustryOrRole[]
            )
          } else {
            if (!newValue) {
              setNameValue('')
            } else {
              setNameValue(chooseDBTranslation(i18n, newValue).name)
            }
            onChange(newValue as unknown as ISkillOrIndustryOrRole & ISkillOrIndustryOrRole[])
          }
          setHelperText(undefined)
          if (resetOnSelect) {
            setComponentValue(null)
            setNameValue('')
          }
        }}
        filterOptions={(options, { inputValue }) => {
          const result = fuseFiltering(options, inputValue, ['translations.name'])

          if (allowCreate && !noPlaceholders && inputValue && inputValue.length > 0) {
            const filteredSkills = skills.filter(
              skill =>
                normalizeOptionName(chooseDBTranslation(i18n, skill)?.name ?? '') === normalizeOptionName(inputValue)
            )
            if (!filteredSkills.length) result.push(inputValue)
          }

          return result
        }}
        getOptionLabel={option => {
          if (typeof option === 'string') {
            return option
          }
          return adminView ? getLabel(option) : chooseDBTranslation(i18n, option).name
        }}
        isOptionEqualToValue={(option, value) => {
          if (typeof option === 'string' || typeof value === 'string') {
            return false
          }

          return option.id === value.id
        }}
        renderOption={(props, option, state) => {
          if (typeof option === 'string') {
            const newProps = { ...props, key: `${t(`placeholder.${type}`)} "${option}"` }
            return [newProps, `${t(`placeholder.${type}`)} "${option}"`, state.index] as React.ReactNode
          }

          return [props, option, state.index] as React.ReactNode
        }}
        renderInput={params => (
          <>
            {helperText && (
              <span
                style={{
                  fontSize: '0.75rem',
                  marginTop: 4,
                }}
              >
                {helperText}
              </span>
            )}
            <InputField
              slotProps={{
                inputLabel: insetLabel ? { shrink: true } : {},
                htmlInput: params?.inputProps,
                input: params?.InputProps,
              }}
              {...params}
              name={`${type}`}
              fullWidth
              placeholder={placeholderLabel ?? placeholderText}
              margin="dense"
              size={size}
              onChange={event => {
                if (
                  !search &&
                  typeof event.target.value === 'string' &&
                  (event.target.value.toLowerCase().includes(',') ||
                    event.target.value.toLowerCase().includes('/') ||
                    event.target.value.toLowerCase().includes(' ja ') ||
                    event.target.value.toLowerCase().includes(' and ') ||
                    event.target.value.toLowerCase().includes('\\'))
                ) {
                  setHelperText(t('skillWarning', { type: t(`${type}Lower`) }))
                } else {
                  setHelperText(undefined)
                }

                setNameValue(event.target.value)
              }}
              variant={placeholderLabel && !placeholderText ? 'standard' : 'outlined'}
              insetLabel={insetLabel}
              label={insetLabel ? label : undefined}
              ariaLabelledBy={!placeholderLabel && !insetLabel ? labelId : undefined}
              required={required}
            />
          </>
        )}
        renderGroup={params => params as unknown as React.ReactNode}
        sx={sx}
        slots={{
          popper: StyledPopper,
        }}
        slotProps={{
          listbox: {
            component: ListboxComponent,
            internalListRef,
            onItemsBuilt: handleItemsBuilt,
          } as any,
        }}
      />
      {didYouMean.dialog}
    </>
  )
}

export default SkillPicker
